"use client";

import { useEffect, useState } from "react";
import { motion } from "motion/react"; // Changed from "motion/react" to "framer-motion" for AnimatePresence
import { Icon } from "@iconify-icon/react";
import { useTranslation } from "react-i18next";

import { SettingsCard } from "../SettingsCard";

import { SubjectPricingDialog } from "./SubjectPricingDialog";

import { toast } from "@/utils/customToast";
import {
  getCurrencySymbol,
  type PriceCurrency,
  type PricingConfiguration,
  type SubjectWithCurriculumLevels,
} from "@/features/calendar/types/pricing";
import { Skeleton } from "@/components/ui/skeleton";
import {
  deletePricingConfiguration,
  getSubjectsWithCurriculumLevels,
} from "@/features/calendar/api/Pricing";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { InformationTooltip } from "@/components/InformationTooltip";

// Removed Collapsible imports: import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";

interface SubjectPricingCardProps {
  isLoading?: boolean;
  subjectPricings: PricingConfiguration[];
  onPricingUpdated: () => void;
}

export const SubjectPricingCard = ({
  isLoading: initialIsLoading = false,
  subjectPricings: initialSubjectPricings,
  onPricingUpdated,
}: SubjectPricingCardProps) => {
  const { t } = useTranslation(["common", "calendar"]);
  const [subjectsDataForContext, setSubjectsDataForContext] = useState<
    SubjectWithCurriculumLevels[]
  >([]);
  const [isLoadingSubjectsData, setIsLoadingSubjectsData] = useState(false);
  const [localPricingsInCard, setLocalPricingsInCard] =
    useState<PricingConfiguration[]>(initialSubjectPricings);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedPricingForDelete, setSelectedPricingForDelete] =
    useState<PricingConfiguration | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    setLocalPricingsInCard(initialSubjectPricings);
  }, [initialSubjectPricings]);

  const fetchSubjectsAndLevelsForContext = async () => {
    try {
      setIsLoadingSubjectsData(true);
      const response = await getSubjectsWithCurriculumLevels();

      setSubjectsDataForContext(response.data.data);
    } catch (error) {
      console.error("Failed to fetch subjects data for context:", error);
    } finally {
      setIsLoadingSubjectsData(false);
    }
  };

  useEffect(() => {
    fetchSubjectsAndLevelsForContext();
  }, []);

  const handleDelete = async () => {
    if (!selectedPricingForDelete) return;
    try {
      setIsDeleting(true);
      await deletePricingConfiguration(selectedPricingForDelete.id);
      toast.success(t("calendar:pricing.subjectPricingRemoved"));
      setLocalPricingsInCard((prev) => prev.filter((p) => p.id !== selectedPricingForDelete.id));
      onPricingUpdated();
    } catch (error) {
      console.error("Failed to delete subject pricing:", error);
      toast.error(t("common:errors.genericErrorTitle"), {
        description: t("common:errors.failedToRemovePricing"),
      });
    } finally {
      setIsDeleting(false);
      setDeleteDialogOpen(false);
      setSelectedPricingForDelete(null);
    }
  };

  const pricingsBySubject = localPricingsInCard.reduce(
    (acc, pricing) => {
      if (!pricing.subject) return acc;
      const subjectId = pricing.subject.id;

      if (!acc[subjectId]) {
        acc[subjectId] = { subject: pricing.subject, pricings: [] };
      }
      acc[subjectId].pricings.push(pricing);

      return acc;
    },
    {} as Record<string, { subject: any; pricings: PricingConfiguration[] }>,
  );

  return (
    <>
      <SettingsCard
        action={
          <InformationTooltip
            description={t("calendar:pricing.subjectPricingTooltip")}
            iconSize="1.25rem"
          />
        }
        bottomCircle={"from-blue-500 to-red-500"}
        description={t("calendar:pricing.subjectSpecificPricingDescription")}
        icon="mdi:book-education-outline"
        title={t("calendar:pricing.subjectSpecificPricing")}
        topCircle={"from-blue-600 to-yellow-500"}
      >
        <div className="space-y-3">
          <motion.button
            className="bg-muted/20 hover:bg-muted/30 group hover:border-accent-foreground/20 relative w-full overflow-hidden rounded-2xl border-transparent p-3 sm:p-4 text-left backdrop-blur-sm transition-all duration-300 hover:shadow-lg"
            onClick={() => setDialogOpen(true)}
          >
            <div className="flex items-center gap-3 sm:gap-6">
              <div className="bg-muted/30 text-accent-foreground flex h-10 w-10 shrink-0 items-center justify-center rounded-xl sm:h-12 sm:w-12 sm:rounded-2xl">
                <Icon className="h-5 w-5 sm:h-6 sm:w-6" height="20" icon="mdi:plus" width="20" />
              </div>
              <div className="min-w-0 flex-1">
                <h3 className="text-accent-foreground mb-0.5 truncate text-sm font-semibold sm:text-base">
                  {t("calendar:pricing.addSubjectPricing")}
                </h3>
                <p className="text-muted-foreground line-clamp-2 text-xs sm:text-sm">
                  {t("calendar:pricing.clickToConfigureSubjectPricing")}
                </p>
              </div>
              <Icon
                className="text-muted-foreground group-hover:text-accent-foreground ml-1 flex-shrink-0 transition-colors sm:ml-3"
                height="16"
                icon="mdi:arrow-right"
                width="16"
              />
            </div>
          </motion.button>

          {initialIsLoading || isLoadingSubjectsData ? (
            <div className="space-y-2">
              {Array(2)
                .fill(0)
                .map((_, i) => (
                  <Skeleton key={i} className="h-12 w-full rounded-lg sm:h-14" />
                ))}
            </div>
          ) : Object.keys(pricingsBySubject).length > 0 ? (
            <div className="space-y-2">
              {Object.values(pricingsBySubject).map(({ subject, pricings }) => {
                if (!subject) return null;

                // Calculate min and max prices for the badge
                const amounts = pricings.map((p) => p.amount);
                const minPrice = Math.min(...amounts);
                const maxPrice = Math.max(...amounts);
                const currency = pricings[0]?.currency as PriceCurrency; // Assuming consistent currency for all pricings of a subject
                const currencySymbol = getCurrencySymbol(currency);

                return (
                  <motion.div
                    key={subject.id}
                    animate={{ opacity: 1 }}
                    className="bg-muted/20 relative overflow-hidden rounded-xl sm:rounded-2xl border-none shadow-lg backdrop-blur-sm"
                    initial={{ opacity: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    {/* Subject Card with Price Range Badge */}
                    <div className="flex w-full items-center gap-3 sm:gap-4 p-3 sm:p-4 pr-16 sm:pr-4 text-left">
                      <div className="bg-muted/30 text-accent-foreground flex h-10 w-10 shrink-0 items-center justify-center rounded-xl sm:h-12 sm:w-12 sm:rounded-2xl">
                        <Icon
                          className="h-5 w-5 sm:h-6 sm:w-6"
                          height="20"
                          icon={subject.icon || "mdi:book-education"}
                          width="20"
                        />
                      </div>
                      <div className="flex min-w-0 flex-1 flex-col items-start gap-1 sm:flex-row sm:items-center sm:gap-2">
                        <h4 className="text-accent-foreground truncate text-left text-sm font-semibold sm:text-base">
                          {subject.name}
                        </h4>
                        {/* Price Range Badge */}
                        <span className="bg-accent text-accent-foreground absolute right-3 top-3 inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium sm:static sm:right-auto sm:top-auto sm:ml-2 sm:text-xs">
                          {currencySymbol}
                          {minPrice.toFixed(2)}
                          {minPrice !== maxPrice && ` - ${maxPrice.toFixed(2)}`}
                        </span>
                      </div>
                    </div>
                    {/* Removed the CollapsibleContent and its children (individual pricings list) */}
                  </motion.div>
                );
              })}
            </div>
          ) : (
            <motion.div
              animate={{ opacity: 1 }}
              className="rounded-lg border border-dashed p-3 sm:p-4 text-center"
              initial={{ opacity: 0 }}
            >
              <Icon
                className="text-accent-foreground mx-auto mb-2 opacity-50 sm:mb-3"
                height="32"
                icon="mdi:book-education-outline"
                width="32"
              />
              <h3 className="text-accent-foreground mb-1 text-xs font-medium sm:text-base">
                {t("calendar:pricing.noSubjectPricingYet")}
              </h3>
              <p className="text-muted-foreground text-[11px] sm:text-sm">
                {t("calendar:pricing.getStartedWithSubjectPricing")}
              </p>
            </motion.div>
          )}
        </div>
      </SettingsCard>

      <SubjectPricingDialog
        existingPricings={localPricingsInCard}
        open={dialogOpen}
        subjects={subjectsDataForContext}
        onOpenChange={setDialogOpen}
        onSuccess={() => {
          onPricingUpdated();
          setDialogOpen(false);
        }}
      />

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("calendar:pricing.removeSubjectPricing")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("calendar:pricing.removeSubjectPricingConfirmation", {
                subject: selectedPricingForDelete?.subject?.name,
                level: selectedPricingForDelete?.curriculum_level?.name || "",
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>{t("common:cancel")}</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={isDeleting}
              onClick={handleDelete}
            >
              {isDeleting ? (
                <>
                  <Icon className="mr-2 animate-spin" height="16" icon="mdi:loading" width="16" />
                  {t("common:deleting")}
                </>
              ) : (
                t("calendar:pricing.removePricing")
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
